import { isoDateFormatter } from "@/src/utils/dateFormatter";
import { stripHTMLTags } from "@/src/utils/strip-html-tags";
import { getArticlePdfUrl, getArticleUrl } from "@/src/utils/paths";

const toPlainText = (value: unknown): string => {
  if (value === null || value === undefined) return "";

  if (Array.isArray(value)) {
    return value.map(toPlainText).filter(Boolean).join(" ");
  }

  if (typeof value === "object") {
    const section = value as { title?: unknown; content?: unknown };
    const title = toPlainText(section.title).replace(/\s*:\s*$/, "");

    return [title, toPlainText(section.content)].filter(Boolean).join(": ");
  }

  return stripHTMLTags(
    String(value)
      .replace(/<[^>]*>/g, " ")
      .replace(/&#(\d+);/g, (_, code) => String.fromCodePoint(Number(code))),
  ).replace(/\s+/g, " ");
};

function downloadFile(content: string, fileName: string, contentType: string) {
  const blob = new Blob([content], { type: contentType });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

export type CitationRecord = {
  fileBaseName?: string;
  citeKey?: string;
  title?: string;
  authors?: string[];
  journalTitle?: string;
  journalAbbreviation?: string;
  publisher?: string;
  volume?: string | number | null;
  issue?: string | number | null;
  year?: string;
  date?: string;
  firstPage?: string | number | null;
  lastPage?: string | number | null;
  articleNumber?: string | null;
  abstract?: string;
  keywords?: string[];
  issn?: string[];
  doi?: string;
  url?: string;
  pdfUrl?: string;
};

const oneLine = (value: unknown): string =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const risLine = (tag: string, value: unknown): string[] => {
  const text = oneLine(value);
  return text ? [`${tag}  - ${text}`] : [];
};

export function buildRisContent(record: CitationRecord): string {
  const pageLines = record.articleNumber
    ? risLine("SP", record.articleNumber)
    : [...risLine("SP", record.firstPage), ...risLine("EP", record.lastPage)];

  return [
    "TY  - JOUR",
    ...risLine("TI", record.title),
    ...(record.authors ?? []).flatMap((author) => risLine("AU", author)),
    ...risLine("JO", record.journalTitle),
    ...risLine("J2", record.journalAbbreviation),
    ...risLine("PB", record.publisher),
    ...risLine("PY", record.year),
    ...risLine("DA", record.date && record.date.replace(/-/g, "/")),
    ...risLine("VL", record.volume),
    ...risLine("IS", record.issue),
    ...pageLines,
    ...risLine("AB", record.abstract),
    ...(record.keywords ?? []).flatMap((keyword) => risLine("KW", keyword)),
    ...(record.issn ?? []).flatMap((issn) => risLine("SN", issn)),
    ...risLine("DO", record.doi),
    ...risLine("UR", record.url),
    ...risLine("L1", record.pdfUrl),
    "ER  - ",
  ].join("\r\n");
}

const BIBTEX_ESCAPES: Record<string, string> = {
  "\\": "\\textbackslash{}",
  "~": "\\textasciitilde{}",
  "^": "\\textasciicircum{}",
  "&": "\\&",
  "%": "\\%",
  $: "\\$",
  "#": "\\#",
  _: "\\_",
  "{": "\\{",
  "}": "\\}",
};

const escapeBibTex = (value: string): string =>
  value.replace(/[\\~^&%$#_{}]/g, (character) => BIBTEX_ESCAPES[character]);

export function buildBibTexContent(record: CitationRecord): string {
  const fields: string[] = [];
  const addField = (name: string, value: unknown, verbatim = false) => {
    const text = oneLine(value);
    if (text)
      fields.push(`  ${name} = {${verbatim ? text : escapeBibTex(text)}},`);
  };

  const pages = record.articleNumber
    ? oneLine(record.articleNumber)
    : record.firstPage && record.lastPage
      ? `${oneLine(record.firstPage)}--${oneLine(record.lastPage)}`
      : oneLine(record.firstPage);

  addField("title", record.title);
  addField("author", (record.authors ?? []).join(" and "));
  addField("journal", record.journalTitle);
  addField("publisher", record.publisher);
  addField("year", record.year);
  addField("volume", record.volume);
  addField("number", record.issue);
  addField("pages", pages);
  addField("issn", (record.issn ?? []).join(", "));
  addField("doi", record.doi, true);
  addField("url", record.url, true);
  addField("abstract", record.abstract);
  addField("keywords", (record.keywords ?? []).join(", "));

  const citeKey =
    oneLine(record.citeKey).replace(/[^A-Za-z0-9:-]+/g, "-") || "citation";

  return [`@article{${citeKey},`, ...fields, "}"].join("\n");
}

const citationFileName = (
  record: CitationRecord,
  extension: string,
): string => {
  const base =
    oneLine(record.fileBaseName).replace(/[^A-Za-z0-9._-]+/g, "-") ||
    "citation";
  return `${base}.${extension}`;
};

export function downloadRis(record: CitationRecord) {
  downloadFile(
    buildRisContent(record),
    citationFileName(record, "ris"),
    "application/x-research-info-systems",
  );
}

export function downloadBibTex(record: CitationRecord) {
  downloadFile(
    buildBibTexContent(record),
    citationFileName(record, "bib"),
    "application/x-bibtex",
  );
}

const formatCitationAuthor = (author: Partial<Author>): string => {
  const given = [author.first_name, author.middle_name]
    .map(oneLine)
    .filter(Boolean)
    .join(" ");
  const family = oneLine(author.last_name);

  if (!family) return given;

  return given ? `${family}, ${given}` : family;
};

const publicationYear = (pubDates?: Article["pub_dates"]): string =>
  [pubDates?.ppub, pubDates?.collection, pubDates?.epub]
    .map((value) => /^\d{4}/.exec(oneLine(value))?.[0] ?? "")
    .find(Boolean) ?? "";

export function articleCitationRecord({
  article,
  journal,
  publisher,
  journalAbbr,
  articleId,
}: {
  article: Article;
  journal: Journal;
  publisher?: string;
  journalAbbr: string;
  articleId: number;
}): CitationRecord {
  const year = publicationYear(article?.pub_dates);
  const firstAuthor = article?.authors?.[0];

  return {
    fileBaseName: [oneLine(firstAuthor?.last_name) || "citation", year]
      .filter(Boolean)
      .join("_"),
    citeKey: `${journalAbbr}-${articleId}`,
    title: toPlainText(article?.title),
    authors: (article?.authors ?? []).map(formatCitationAuthor).filter(Boolean),
    journalTitle: toPlainText(journal?.title),
    journalAbbreviation: toPlainText(journal?.meta_data?.nlm_ta),
    publisher,
    volume: article?.volume_name,
    issue: article?.issue_name,
    year,
    date: isoDateFormatter(article?.pub_dates?.epub),
    firstPage: article?.first_page,
    lastPage: article?.last_page,
    articleNumber: article?.elocation_id,
    abstract: toPlainText(article?.abstract),
    keywords: (article?.keywords ?? []).map(toPlainText).filter(Boolean),
    issn: [journal?.meta_data?.issn_ppub, journal?.meta_data?.issn_epub]
      .map(oneLine)
      .filter(Boolean),
    doi: article?.doi,
    url: getArticleUrl(journalAbbr, articleId, true),
    pdfUrl: getArticlePdfUrl(journalAbbr, articleId, true),
  };
}

export function exportToEndNote(article: ArticleCardData) {
  const firstAuthor: string = article.authors?.[0] ?? "";
  const publicationDate = isoDateFormatter(article.epub);

  downloadRis({
    fileBaseName: [
      firstAuthor.split(" ").pop() || "citation",
      publicationDate.slice(0, 4),
    ]
      .filter(Boolean)
      .join("_"),
    title: toPlainText(article.title),
    authors: article.authors ?? [],
    journalTitle: toPlainText(article.journal?.title),
    year: publicationDate.slice(0, 4),
    date: publicationDate,
    volume: article.volume,
    issue: article.issue,
    abstract: toPlainText(article.abstract),
    keywords: (article.keywords ?? []).map(toPlainText).filter(Boolean),
    doi: article.doi,
  });
}

const escapeCsvField = (field: any): string => {
  const stringField = String(field ?? "");
  if (
    stringField.includes(",") ||
    stringField.includes('"') ||
    stringField.includes("\n") ||
    stringField.includes("\r")
  ) {
    return `"${stringField.replace(/"/g, '""')}"`;
  }
  return stringField;
};

export function exportToCsv(articles: ArticleCardData[]) {
  const headers = [
    "Title",
    "Authors",
    "Journal",
    "Publication Date",
    "DOI",
    "Volume",
    "Issue",
    "Abstract",
    "Keywords",
  ];

  const rows = articles.map((article) =>
    [
      escapeCsvField(toPlainText(article.title)),
      escapeCsvField((article.authors ?? []).join("; ")),
      escapeCsvField(toPlainText(article.journal?.title)),
      escapeCsvField(isoDateFormatter(article.epub)),
      escapeCsvField(article.doi),
      escapeCsvField(article.volume),
      escapeCsvField(article.issue),
      escapeCsvField(toPlainText(article.abstract)),
      escapeCsvField(
        (article.keywords ?? []).map((keyword) => keyword.trim()).join("; "),
      ),
    ].join(","),
  );

  const csvContent = [headers.join(","), ...rows].join("\r\n");
  const fileName = `export_${new Date().toISOString().split("T")[0]}.csv`;
  downloadFile(csvContent, fileName, "text/csv;charset=utf-8;");
}
